import React, { useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { 
  Box, Typography, Table, TableBody, TableCell, TableContainer, 
  TableHead, TableRow, Paper, Checkbox, Button, ToggleButton, 
  ToggleButtonGroup, TextField, Tooltip
} from '@mui/material';
import NavLink from './common/NavLink';
import { slugify } from '../utils/slugify'; 
import useTeamCompetitionData from './useTeamCompetitionData';

const LeagueTables = () => {
  const { id } = useParams();
  console.log('League ID from useParams:', id);

  const { teams, matches, name, group, loading: teamsLoading, error: teamsError } = useTeamCompetitionData(id);

  const [selectedTeams, setSelectedTeams] = useState(new Set());
  const [tableFilter, setTableFilter] = useState('all');
  const [formMatches, setFormMatches] = useState(5);
  const [partialTable, setPartialTable] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Bezpieczne ustalenie nazwy ligi do wyświetlenia
  const leagueNameDisplay = useMemo(() => {
    if (name) return name;
    if (teams && teams.length > 0 && teams[0].competitionId) {
      return teams[0].competitionId; 
    }
    return 'Liga';
  }, [name, teams]);

  // Filtrowanie meczów ze względu na wybrane ramy czasowe (używane do tabeli oraz kafelków formy)
  const filteredMatchesByDate = useMemo(() => {
    if (!matches) return [];
    
    return matches.filter(match => {
      if (!match.matchDate) return true;
      
      const matchDateObj = new Date(match.matchDate.replace(' ', 'T'));
      if (isNaN(matchDateObj.getTime())) return true;
      
      const matchTime = matchDateObj.getTime();
      let isValid = true;
      
      if (startDate) {
        const startTimestamp = new Date(`${startDate}T00:00:00`).getTime();
        if (matchTime < startTimestamp) isValid = false;
      }
      
      if (endDate) {
        const endTimestamp = new Date(`${endDate}T23:59:59`).getTime();
        if (matchTime > endTimestamp) isValid = false;
      }
      
      return isValid;
    });
  }, [matches, startDate, endDate]);

  // Funkcja wyciągająca formę oraz najbliższy zaplanowany mecz z uwzględnieniem filtrów i zakresu dat (Punkt 2)
  const getTeamFormAndNextMatch = (teamId, teamName, baseMatchesList, currentFilter) => {
    const resultData = {
      historyForm: [],
      nextMatch: null
    };

    if (!baseMatchesList) return resultData;

    // Uniwersalna funkcja filtrująca mecze ze względu na to, czy zespół grał jako gospodarz czy gość
    const filterMatchesBySide = (list) => {
      return list.filter(match => {
        if (currentFilter === 'all') return true;
        if (currentFilter === 'home') return match.homeTeamId === teamId;
        if (currentFilter === 'away') return match.awayTeamId === teamId;
        return true;
      });
    };

    // Lista meczów uwzględniająca filtry lokalizacji (oraz przekazany zakres dat)
    const eligibleMatches = filterMatchesBySide(baseMatchesList);

    // 1. Szukamy najbliższego zaplanowanego meczu
    const upcomingMatches = eligibleMatches
      .filter(match => match.status === 'planned' && (match.homeTeamId === teamId || match.awayTeamId === teamId))
      .sort((a, b) => {
        if (a.matchDate && !b.matchDate) return -1;
        if (!a.matchDate && b.matchDate) return 1;
        
        if (a.matchDate && b.matchDate) {
          const dateA = new Date(a.matchDate.replace(' ', 'T')).getTime();
          const dateB = new Date(b.matchDate.replace(' ', 'T')).getTime();
          return dateA - dateB;
        }
        
        const roundA = parseInt(a.round) || 0;
        const roundB = parseInt(b.round) || 0;
        return roundA - roundB;
      });

    if (upcomingMatches.length > 0) {
      const nextM = upcomingMatches[0];
      const displayDate = nextM.matchDate ? nextM.matchDate.split(' ')[0] : `Kolejka ${nextM.round}`;
      const isHomeCurrent = nextM.homeTeamId === teamId;

      resultData.nextMatch = {
        result: '?',
        color: '#2980b9', 
        tooltip: (
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.9rem', mb: 0.5 }}>?:?</Typography>
            <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
              <span style={{ fontWeight: isHomeCurrent ? 800 : 400, color: isHomeCurrent ? '#ffffff' : '#aaa' }}>{nextM.homeTeamName}</span>
              {' - '}
              <span style={{ fontWeight: !isHomeCurrent ? 800 : 400, color: !isHomeCurrent ? '#ffffff' : '#aaa' }}>{nextM.awayTeamName}</span>
            </Typography>
            <Typography variant="caption" sx={{ color: '#aaa', display: 'block' }}>{displayDate}</Typography>
          </Box>
        )
      };
    }

    // 2. Szukamy historii ostatnich rozegranych meczów w wybranym zakresie
    const historicalResults = eligibleMatches
      .filter(match => match.status === 'ended' && (match.homeTeamId === teamId || match.awayTeamId === teamId))
      .sort((a, b) => {
        const dateA = a.matchDate ? new Date(a.matchDate.replace(' ', 'T')) : new Date(0);
        const dateB = b.matchDate ? new Date(b.matchDate.replace(' ', 'T')) : new Date(0);
        return dateB - dateA; 
      })
      .slice(0, 5) 
      .map(match => {
        const isHome = match.homeTeamId === teamId;
        const teamGoals = isHome ? match.homeGoals : match.awayGoals;
        const opponentGoals = isHome ? match.awayGoals : match.homeGoals;
        const displayDate = match.matchDate ? match.matchDate.split(' ')[0] : '';
        
        const tooltipContent = (
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.9rem', mb: 0 }}>
              {match.homeGoals}:{match.awayGoals}
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', mb: 0 }}>
              <span style={{ fontWeight: isHome ? 800 : 400, color: isHome ? '#ffffff' : '#aaa' }}>{match.homeTeamName}</span>
              {' - '}
              <span style={{ fontWeight: !isHome ? 800 : 400, color: !isHome ? '#ffffff' : '#aaa' }}>{match.awayTeamName}</span>
            </Typography>
            <Typography variant="caption" sx={{ color: '#aaa', display: 'block' }}>
              {displayDate}
            </Typography>
          </Box>
        );

        if (teamGoals > opponentGoals) {
          return { result: 'Z', color: '#1f8a4c', tooltip: tooltipContent };
        }
        if (teamGoals === opponentGoals) {
          return { result: 'R', color: '#a8a8a8', tooltip: tooltipContent };
        }
        return { result: 'P', color: '#c0392b', tooltip: tooltipContent };
      });

    resultData.historyForm = historicalResults;
    return resultData;
  };

  if (teamsLoading) {
    return (
      <Box sx={{ textAlign: 'center', padding: '20px' }}>
        <Typography variant="h6">Ładowanie...</Typography>
      </Box>
    );
  }

  if (teamsError) {
    return (
      <Box sx={{ textAlign: 'center', padding: '20px' }}>
        <Typography variant="h6">{teamsError}</Typography>
      </Box>
    );
  }

  const calculateTeamStats = (matchesToConsider, filter) => {
    if (!teams || teams.length === 0) return [];

    const teamStats = teams.map(team => {
      // Przekazujemy przefiltrowaną liście meczów, aby kafelki respektowały daty z kalendarza (Punkt 2)
      const { historyForm, nextMatch } = getTeamFormAndNextMatch(team.teamId, team.teamName, matchesToConsider, filter);

      return {
        teamId: team.teamId,
        teamName: team.teamName,
        points: 0,
        matchesPlayed: 0,
        wins: 0,
        draws: 0,
        losses: 0,
        goalsScored: 0,
        goalsConceded: 0,
        form: historyForm,
        nextMatch: nextMatch
      };
    });

    const teamStatsMap = new Map(teamStats.map(stat => [stat.teamId, stat]));

    if (!matchesToConsider || matchesToConsider.length === 0) {
      return Array.from(teamStatsMap.values()).sort((a, b) => b.points - a.points);
    }

    matchesToConsider.forEach(match => {
      if (match.status !== 'ended') return;

      const homeTeamId = match.homeTeamId;
      const awayTeamId = match.awayTeamId;
      const homeGoals = match.homeGoals;
      const awayGoals = match.awayGoals;

      const homeTeamStats = teamStatsMap.get(homeTeamId);
      const awayTeamStats = teamStatsMap.get(awayTeamId);

      if (!homeTeamStats || !awayTeamStats) return;

      let includeMatch = false;
      if (filter === 'all') includeMatch = true;
      else if (filter === 'home' && match.homeTeamId) includeMatch = true;
      else if (filter === 'away' && match.awayTeamId) includeMatch = true;

      if (!includeMatch) return;

      if (filter === 'all' || filter === 'home') {
        homeTeamStats.matchesPlayed += 1;
        homeTeamStats.goalsScored += homeGoals;
        homeTeamStats.goalsConceded += awayGoals;

        if (homeGoals > awayGoals) {
          homeTeamStats.points += 3;
          homeTeamStats.wins += 1;
        } else if (homeGoals === awayGoals) {
          homeTeamStats.points += 1;
          homeTeamStats.draws += 1;
        } else {
          homeTeamStats.losses += 1;
        }
      }

      if (filter === 'all' || filter === 'away') {
        awayTeamStats.matchesPlayed += 1;
        awayTeamStats.goalsScored += awayGoals;
        awayTeamStats.goalsConceded += homeGoals;

        if (awayGoals > homeGoals) {
          awayTeamStats.points += 3;
          awayTeamStats.wins += 1;
        } else if (awayGoals === homeGoals) {
          awayTeamStats.points += 1;
          awayTeamStats.draws += 1;
        } else {
          awayTeamStats.losses += 1;
        }
      }
    });

    return Array.from(teamStatsMap.values()).sort((a, b) => {
      if (b.points !== a.points) return b.points - a.points;
      const aGoalDiff = a.goalsScored - a.goalsConceded;
      const bGoalDiff = b.goalsScored - b.goalsConceded;
      if (bGoalDiff !== aGoalDiff) return bGoalDiff - aGoalDiff;
      return b.goalsScored - a.goalsScored;
    });
  };

  const teamStats = calculateTeamStats(filteredMatchesByDate, tableFilter);

  const calculatePartialTable = () => {
    if (selectedTeams.size < 2) {
      setPartialTable(null);
      return;
    }

    const filteredMatches = filteredMatchesByDate.filter(match => 
      selectedTeams.has(match.homeTeamId) && selectedTeams.has(match.awayTeamId)
    );

    const partialStats = calculateTeamStats(filteredMatches, 'all');
    setPartialTable(partialStats.filter(stat => selectedTeams.has(stat.teamId)));
  };

  const handleTeamSelect = (teamId) => {
    const newSelectedTeams = new Set(selectedTeams);
    if (newSelectedTeams.has(teamId)) {
      newSelectedTeams.delete(teamId);
    } else {
      newSelectedTeams.add(teamId);
    }
    setSelectedTeams(newSelectedTeams);
  };

  const handleFilterChange = (event, newFilter) => {
    if (newFilter) {
      setTableFilter(newFilter);
    }
  };

  const darkDatePickerStyles = {
    backgroundColor: '#2c2c2c',
    borderRadius: 1,
    '& .MuiInputBase-input': { color: '#ffffff' },
    '& .MuiInputLabel-root': { color: '#aaaaaa' },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#555555' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#888888' },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#ffffff' },
    '& .MuiInputLabel-root.Mui-focused': { color: '#ffffff' },
    '& input::-webkit-calendar-picker-indicator': {
      filter: 'invert(1)', 
      cursor: 'pointer'
    }
  };

  const renderTable = (stats, isPartial = false) => {
    if (!stats || stats.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', padding: '10px' }}>
          <Typography variant="h6" sx={{ color: '#aaa' }}>Brak danych do wyświetlenia w tym zakresie dat.</Typography>
        </Box>
      );
    }

    return (
      <TableContainer 
        component={Paper} 
        sx={{ 
          backgroundColor: '#2c2c2c', 
          // === KLUCZOWA POPRAWKA RESPONSYWNOŚCI ===
          width: { 
            xs: '100%',          // Na telefonach (xs) kontener zajmuje dokładnie 100% szerokości ekranu
            sm: 'max-content'    // Na większych ekranach (sm, md...) zwęża się idealnie do szerokości danych
          },
          maxWidth: '100%',      // Blokada przed wyjeżdżaniem poza nadrzędny div/ekran
          margin: '0 auto',      // Środkuje zbitą tabelę na komputerach
          overflowX: 'auto',     // Włącza wewnętrzny scroll na telefonach
          WebkitOverflowScrolling: 'touch', // Płynny scroll na iOS
          position: 'relative',
          borderRadius: '12px',
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.3)',
          
          '&::-webkit-scrollbar': { width: '8px', height: '8px' },
          '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: '4px' },
          '&::before': {
            content: '"SymuLiga.pl"',
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%) rotate(-45deg)',
            fontSize: { xs: '3rem', sm: '4rem', md: '5rem' },
            fontWeight: 'bold',
            color: 'rgba(255, 255, 255, 0.03)', // Delikatny znak wodny dopasowany do dark mode
            pointerEvents: 'none',
            zIndex: 0,
            whiteSpace: 'nowrap',
          },
        }}
      >
        <Table 
          sx={{ 
            // Wymuszamy, aby tabela wewnętrznie zawsze budowała pełną szerokość swoich kolumn,
            // dzięki czemu na telefonie wyjdzie poza kontener i aktywuje scroll, a na desktopie wypełni go idealnie.
            width: 'max-content',    
            tableLayout: 'auto', 
            borderCollapse: 'collapse',
            position: 'relative',
            zIndex: 1
          }} 
          aria-label={isPartial ? "partial league table" : "league table"}
        >
          <TableHead>
            <TableRow>
              {/* Padding wiersza nagłówka zmieniony na 2px w pionie (Punkt 1) */}
              {!isPartial && (
                <TableCell sx={{ color: '#ffffff', borderBottom: '1px solid #3a3a3a', fontWeight: 'bold', padding: '0px 0px', lineHeight: 1.1 }}></TableCell>
              )}
              <TableCell sx={{ color: '#ffffff', borderBottom: '1px solid #3a3a3a', fontWeight: 'bold', padding: '4px 6px', textAlign: 'center', lineHeight: 1.1 }}>#</TableCell>
              <TableCell sx={{ color: '#ffffff', borderBottom: '1px solid #3a3a3a', fontWeight: 'bold', padding: '4px 6px', lineHeight: 1.1 }}>Drużyna</TableCell>
              <TableCell sx={{ color: '#ffffff', borderBottom: '1px solid #3a3a3a', fontWeight: 'bold', padding: '4px 6px', textAlign: 'center', lineHeight: 1.1 }}>M</TableCell>
              <TableCell sx={{ color: '#ffffff', borderBottom: '1px solid #3a3a3a', fontWeight: 'bold', padding: '4px 6px', textAlign: 'center', lineHeight: 1.1 }}>Pkt</TableCell>
              <TableCell sx={{ color: '#ffffff', borderBottom: '1px solid #3a3a3a', fontWeight: 'bold', padding: '4px 6px', textAlign: 'center', lineHeight: 1.1 }}>Z</TableCell>
              <TableCell sx={{ color: '#ffffff', borderBottom: '1px solid #3a3a3a', fontWeight: 'bold', padding: '4px 6px', textAlign: 'center', lineHeight: 1.1 }}>R</TableCell>
              <TableCell sx={{ color: '#ffffff', borderBottom: '1px solid #3a3a3a', fontWeight: 'bold', padding: '4px 6px', textAlign: 'center', lineHeight: 1.1 }}>P</TableCell>
              <TableCell sx={{ color: '#ffffff', borderBottom: '1px solid #3a3a3a', fontWeight: 'bold', padding: '4px 6px', textAlign: 'center', lineHeight: 1.1 }}>Br</TableCell>
              {!isPartial && (
                <TableCell sx={{ color: '#ffffff', borderBottom: '1px solid #3a3a3a', fontWeight: 'bold', padding: '2px 12px', textAlign: 'center', lineHeight: 1.1 }}>Forma</TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {stats.map((team, index) => (
              <TableRow
                key={team.teamId}
                sx={{
                  backgroundColor: 'transparent',
                  '&:hover': { backgroundColor: '#3a3a3a' },
                }}
              >
                {/* Padding wierszy ciała ustawiony na 1px w pionie (Punkt 1) */}
                {!isPartial && (
                  <TableCell sx={{ color: '#ffffff', borderBottom: '1px solid #3a3a3a', padding: '1px 1px', height: '26px' }}>
                    <Checkbox
                      checked={selectedTeams.has(team.teamId)}
                      onChange={() => handleTeamSelect(team.teamId)}
                      sx={{ 
                        color: '#ffffff', 
                        '&.Mui-checked': { color: '#ffffff' },
                        padding: '2px',
                        '& .MuiTouchRipple-root': {
                          width: '24px',
                          height: '24px',
                          left: 'calc(50% - 12px)',
                          top: 'calc(50% - 12px)'
                        }
                      }}
                    />
                  </TableCell>
                )}
                <TableCell sx={{ color: '#ffffff', borderBottom: '1px solid #3a3a3a', padding: '1px 6px', textAlign: 'center', lineHeight: 1.1 }}>{index + 1}</TableCell>
                <TableCell component={NavLink} to={`/team/${team.teamId}/${slugify(team.teamName)}`} sx={{ color: '#ffffff', borderBottom: '1px solid #3a3a3a', padding: '1px 6px', lineHeight: 1.1 }}>
                  {team.teamName}
                </TableCell>
                <TableCell sx={{ color: '#ffffff', borderBottom: '1px solid #3a3a3a', padding: '1px 6px', textAlign: 'center', lineHeight: 1.1 }}>{team.matchesPlayed}</TableCell>
                <TableCell sx={{ color: '#ffffff', borderBottom: '1px solid #3a3a3a', padding: '1px 6px', textAlign: 'center', lineHeight: 1.1 }}>{team.points}</TableCell>
                <TableCell sx={{ color: '#ffffff', borderBottom: '1px solid #3a3a3a', padding: '1px 6px', textAlign: 'center', lineHeight: 1.1 }}>{team.wins}</TableCell>
                <TableCell sx={{ color: '#ffffff', borderBottom: '1px solid #3a3a3a', padding: '1px 6px', textAlign: 'center', lineHeight: 1.1 }}>{team.draws}</TableCell>
                <TableCell sx={{ color: '#ffffff', borderBottom: '1px solid #3a3a3a', padding: '1px 6px', textAlign: 'center', lineHeight: 1.1 }}>{team.losses}</TableCell>
                <TableCell sx={{ color: '#ffffff', borderBottom: '1px solid #3a3a3a', padding: '1px 6px', textAlign: 'center', lineHeight: 1.1 }}>
                  {team.goalsScored}:{team.goalsConceded}
                </TableCell>
                
                {!isPartial && (
                  <TableCell sx={{ borderBottom: '1px solid #3a3a3a', padding: '1px 12px' }}>
                    <Box sx={{ display: 'flex', gap: '5px', justifyContent: 'flex-start', alignItems: 'center', py: '2px' }}>
                      
                      {/* Przyszły mecz */}
                      {team.nextMatch ? (
                        <Tooltip 
                          title={team.nextMatch.tooltip} 
                          arrow 
                          placement="top"
                          enterDelay={0} 
                          leaveDelay={0} 
                          disableInteractive
                          componentsProps={{
                            tooltip: {
                              sx: {
                                backgroundColor: '#111111',
                                color: '#ffffff',
                                fontSize: '0.75rem',
                                border: '1px solid #444444',
                                boxShadow: '0px 2px 10px rgba(0,0,0,0.5)',
                                p: 1
                              }
                            },
                            arrow: { sx: { color: '#111111' } }
                          }}
                        >
                          <Box
                            sx={{
                              width: '20px',
                              height: '20px',
                              backgroundColor: team.nextMatch.color,
                              color: '#ffffff',
                              borderRadius: '4px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '11px',
                              fontWeight: 'bold',
                              cursor: 'pointer'
                            }}
                          >
                            {team.nextMatch.result}
                          </Box>
                        </Tooltip>
                      ) : (
                        <Box sx={{ width: '20px', height: '20px' }} />
                      )}

                      {/* Rozegrane mecze (zależne teraz od wybranego zakresu dat) */}
                      {team.form && team.form.length > 0 ? (
                        team.form.map((f, i) => (
                          <Tooltip 
                            key={i} 
                            title={f.tooltip} 
                            arrow 
                            placement="top"
                            enterDelay={0} 
                            leaveDelay={0} 
                            disableInteractive
                            componentsProps={{
                              tooltip: {
                                sx: {
                                  backgroundColor: '#111111',
                                  color: '#ffffff',
                                  fontSize: '0.75rem',
                                  border: '1px solid #444444',
                                  boxShadow: '0px 2px 10px rgba(0,0,0,0.5)',
                                  p: 1
                                }
                              },
                              arrow: { sx: { color: '#111111' } }
                            }}
                          >
                            <Box
                              sx={{
                                width: '20px',
                                height: '20px',
                                backgroundColor: f.color,
                                color: '#ffffff',
                                borderRadius: '4px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '11px',
                                fontWeight: 'bold',
                                textTransform: 'uppercase',
                                cursor: 'pointer'
                              }}
                            >
                              {f.result}
                            </Box>
                          </Tooltip>
                        ))
                      ) : null}
                    </Box>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Box sx={{ padding: { xs: '2px', md: '10px' } }}>
      <Typography variant="h5" sx={{ textAlign: 'center', padding: '10px 10px', fontSize: { xs: '1.2rem', sm: '1.5rem' }, fontWeight: 'bold', color: '#ffffff', mb: 3 }}>
        Aktualna tabela — {leagueNameDisplay}
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, mb: 4 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
          <TextField
            label="Data od"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
            sx={darkDatePickerStyles}
          />

          <TextField
            label="Data do"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
            sx={darkDatePickerStyles}
          />
        </Box>

        <ToggleButtonGroup
          value={tableFilter}
          exclusive
          onChange={handleFilterChange}
          aria-label="table filter"
          sx={{ backgroundColor: '#2c2c2c' }}
        >
          <ToggleButton value="all" sx={{ color: '#ffffff', '&.Mui-selected': { backgroundColor: '#3a3a3a', color: '#ffffff' } }}>
            Wszystkie
          </ToggleButton>
          <ToggleButton value="home" sx={{ color: '#ffffff', '&.Mui-selected': { backgroundColor: '#3a3a3a', color: '#ffffff' } }}>
            Dom
          </ToggleButton>
          <ToggleButton value="away" sx={{ color: '#ffffff', '&.Mui-selected': { backgroundColor: '#3a3a3a', color: '#ffffff' } }}>
            Wyjazd
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {renderTable(teamStats)}

      <Box sx={{ textAlign: 'center', mt: 3 }}>
        <Button
          variant="contained"
          onClick={calculatePartialTable}
          disabled={selectedTeams.size < 2}
          sx={{
            backgroundColor: selectedTeams.size < 2 ? '#555' : '#1976d2',
            '&:hover': { backgroundColor: selectedTeams.size < 2 ? '#555' : '#1565c0' },
          }}
        >
          Tabela częściowa
        </Button>
      </Box>

      {partialTable && (
        <>
          <Typography variant="h6" sx={{ mt: 5, mb: 2, textAlign: 'center', color: '#ffffff' }}>
            Tabela częściowa (mecze między wybranymi drużynami)
          </Typography>
          {renderTable(partialTable, true)}
        </>
      )}
    </Box>
  );
};

export default LeagueTables;